import { useMemo } from "react";
import { Account, TransactionDetailValue, TransactionCustomFieldValues } from "../../../types";
import { TransactionWithDate } from "../../../utils/transactionProcessing";
import { validateAmountDecimalPlaces } from "../../../utils/currencyDefaults";

interface FormData {
  title: string;
  category: string;
  amount: string;
  currency: string;
  rate: string;
  defaultCurrencyRate: string;
  date: string;
  time: string;
  accountId: string;
  accountGroup: string;
  details?: TransactionDetailValue;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
  feeAmount?: string;
  feeAccountId?: string;
}

interface TransferFormData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  date: string;
  time: string;
  transferRate: string;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
  feeAmount?: string;
  feeAccountId?: string;
}

interface UseFormValidationProps {
  formData: FormData;
  transferFormData: TransferFormData;
  editingTransaction: TransactionWithDate | null;
  initialFormData: FormData | null;
  initialTransferFormData: TransferFormData | null;
  accounts: Account[];
  defaultCurrency: string;
  currencies?: Array<{ code: string; decimalPlaces?: number }>;
}

export const useFormValidation = ({
  formData,
  transferFormData,
  editingTransaction,
  initialFormData,
  initialTransferFormData,
  accounts,
  defaultCurrency,
  currencies = [],
}: UseFormValidationProps) => {
  // Validate transaction form (expense/income)
  const isTransactionFormValid = useMemo(() => {
    if (!formData.category || formData.category.trim() === "") return false;
    if (!formData.date || formData.date.trim() === "") return false;
    if (!formData.accountId || formData.accountId.trim() === "") return false;
    if (!formData.amount || formData.amount.trim() === "" || parseFloat(formData.amount) < 0) return false;
    
    // Check currency conversion rate if currencies differ
    const account = accounts.find((acc) => acc.id === formData.accountId);
    const accountCurrency = account?.currency || defaultCurrency;
    const expenseCurrency = formData.currency || accountCurrency;
    
    if (expenseCurrency !== accountCurrency) {
      if (!formData.rate || formData.rate.trim() === "" || parseFloat(formData.rate) <= 0) {
        return false;
      }
    }
    // If fee amount is set and > 0, fee account is required
    const feeAmt = formData.feeAmount != null && formData.feeAmount.trim() !== "" ? parseFloat(formData.feeAmount) : NaN;
    if (!isNaN(feeAmt) && feeAmt > 0 && (!formData.feeAccountId || formData.feeAccountId.trim() === "")) {
      return false;
    }
    // Fee amount decimal places must match fee account currency (when fee account is selected)
    if (formData.feeAccountId && formData.feeAmount != null && formData.feeAmount.trim() !== "" && currencies.length > 0) {
      const feeAccount = accounts.find((acc) => acc.id === formData.feeAccountId);
      const feeCurrency = feeAccount?.currency || defaultCurrency;
      const feeValidation = validateAmountDecimalPlaces(formData.feeAmount, feeCurrency, currencies, defaultCurrency);
      if (!feeValidation.isValid) return false;
    }
    return true;
  }, [formData, accounts, defaultCurrency, currencies]);

  // Validate transfer form
  const isTransferFormValid = useMemo(() => {
    if (!transferFormData.date || transferFormData.date.trim() === "") return false;
    if (!transferFormData.fromAccountId || transferFormData.fromAccountId.trim() === "") return false;
    if (!transferFormData.toAccountId || transferFormData.toAccountId.trim() === "") return false;
    if (!transferFormData.amount || transferFormData.amount.trim() === "" || parseFloat(transferFormData.amount) < 0) return false;
    
    // Check if currencies differ and rate is required
    const fromAccount = accounts.find((acc) => acc.id === transferFormData.fromAccountId);
    const toAccount = accounts.find((acc) => acc.id === transferFormData.toAccountId);
    if (!fromAccount || !toAccount) return false;
    
    if (fromAccount.currency !== toAccount.currency) {
      if (!transferFormData.transferRate || transferFormData.transferRate.trim() === "" || parseFloat(transferFormData.transferRate) <= 0) {
        return false;
      }
    }
    // Transfer fee amount decimal places must match fee account currency (when fee account is selected)
    if (transferFormData.feeAccountId && transferFormData.feeAmount != null && transferFormData.feeAmount.trim() !== "" && currencies.length > 0) {
      const feeAccount = accounts.find((acc) => acc.id === transferFormData.feeAccountId);
      const feeCurrency = feeAccount?.currency || defaultCurrency;
      const feeValidation = validateAmountDecimalPlaces(transferFormData.feeAmount, feeCurrency, currencies, defaultCurrency);
      if (!feeValidation.isValid) return false;
    }
    return true;
  }, [transferFormData, accounts, defaultCurrency, currencies]);

  // Check if transaction form has changes when editing (compare against initial form data)
  const hasTransactionChanges = (): boolean => {
    // If not editing, always return true (allow for new transactions)
    if (!editingTransaction || editingTransaction.type === "transfer") return true;
    
    // If editing but no initial data yet, return false (keep Update button disabled until form is initialized)
    if (!initialFormData) return false;
    
    // Normalize amount/rate for comparison (avoid "100" !== "100.0")
    const normalizeNum = (s: string | undefined): string => {
      if (!s || s.trim() === "") return "";
      const n = parseFloat(s);
      return isNaN(n) ? (s || "").trim() : n.toString();
    };
    const currentAmount = normalizeNum(formData.amount) || "0";
    const initialAmount = normalizeNum(initialFormData.amount) || "0";
    const currentRate = normalizeNum((formData.rate || "").trim());
    const initialRate = normalizeNum((initialFormData.rate || "").trim());
    const currentDefaultRate = normalizeNum((formData.defaultCurrencyRate || "").trim());
    const initialDefaultRate = normalizeNum((initialFormData.defaultCurrencyRate || "").trim());
    
    // Compare basic fields
    const basicFieldsChanged = (
      (formData.title || "").trim() !== (initialFormData.title || "").trim() ||
      (formData.category || "").trim() !== (initialFormData.category || "").trim() ||
      currentAmount !== initialAmount ||
      formData.date !== initialFormData.date ||
      formData.time !== initialFormData.time ||
      formData.accountId !== initialFormData.accountId ||
      formData.currency !== initialFormData.currency ||
      currentRate !== initialRate ||
      currentDefaultRate !== initialDefaultRate
    );
    const feeFieldsChanged = (
      (formData.feeAmount ?? "").trim() !== (initialFormData.feeAmount ?? "").trim() ||
      (formData.feeAccountId ?? "") !== (initialFormData.feeAccountId ?? "")
    );
    if (basicFieldsChanged || feeFieldsChanged) return true;
    
    // Compare details - check if details object changed
    const currentDetails = formData.details || {};
    const initialDetails = initialFormData.details || {};
    
    // Check if any detail value changed or was added
    // Get all unique detail keys from both current and initial
    const allDetailKeys = Array.from(new Set([
      ...Object.keys(currentDetails),
      ...Object.keys(initialDetails),
    ]));
    
    // Check if any detail value changed
    for (const key of allDetailKeys) {
      const currentValue = currentDetails[key];
      const initialValue = initialDetails[key];
      
      // Normalize values: undefined, null, and empty string all represent "no value"
      const normalizeValue = (val: any): any => {
        if (val === undefined || val === null || val === "") {
          return undefined;
        }
        // For numbers, compare as numbers
        if (typeof val === "number") {
          return val;
        }
        // For strings, compare trimmed strings
        if (typeof val === "string") {
          return val.trim() || undefined;
        }
        return val;
      };
      
      const normalizedCurrent = normalizeValue(currentValue);
      const normalizedInitial = normalizeValue(initialValue);
      
      if (normalizedCurrent !== normalizedInitial) {
        return true; // Detail changed
      }
    }

    // Compare custom field values
    const currentCustom = formData.customFieldValues || {};
    const initialCustom = initialFormData.customFieldValues || {};
    const customKeys = Array.from(new Set([...Object.keys(currentCustom), ...Object.keys(initialCustom)]));
    for (let ki = 0; ki < customKeys.length; ki++) {
      const key = customKeys[ki];
      const c = currentCustom[key];
      const i = initialCustom[key];
      const cStr = typeof c === "string" ? c : (c && typeof c === "object" && "url" in c ? (c as { url?: string }).url : undefined);
      const iStr = typeof i === "string" ? i : (i && typeof i === "object" && "url" in i ? (i as { url?: string }).url : undefined);
      if (cStr !== iStr) return true;
    }
    if (formData.customFieldPendingFiles && Object.keys(formData.customFieldPendingFiles).length > 0) return true;
    
    return false; // No changes detected
  };

  // Check if transfer form has changes when editing (compare against initial form data)
  const hasTransferChanges = (): boolean => {
    // If not editing or not editing a transfer, always return true (allow for new transfers)
    if (!editingTransaction || editingTransaction.type !== "transfer") return true;
    
    // If editing but no initial data yet, return false (no changes, disable button)
    if (!initialTransferFormData) return false;
    
    // Normalize amounts for comparison (handle empty strings and number formatting)
    const normalizeAmount = (amount: string | undefined): string => {
      if (!amount || amount.trim() === "") return "0";
      const parsed = parseFloat(amount);
      return isNaN(parsed) ? "0" : parsed.toString();
    };
    
    const currentAmount = normalizeAmount(transferFormData.amount);
    const initialAmount = normalizeAmount(initialTransferFormData.amount);
    
    // Normalize fee amounts for comparison
    const normalizeFeeAmount = (amount: string | undefined): string => {
      if (!amount || amount.trim() === "") return "";
      const parsed = parseFloat(amount);
      return isNaN(parsed) ? "" : parsed.toString();
    };
    
    const currentFeeAmount = normalizeFeeAmount(transferFormData.feeAmount);
    const initialFeeAmount = normalizeFeeAmount(initialTransferFormData.feeAmount);
    
    // Normalize fee account IDs (empty string and undefined are treated the same)
    const currentFeeAccountId = transferFormData.feeAccountId || "";
    const initialFeeAccountId = initialTransferFormData.feeAccountId || "";
    
    // Normalize transfer rate for comparison
    const normalizeRate = (rate: string | undefined): string => {
      if (!rate || rate.trim() === "") return "";
      const parsed = parseFloat(rate);
      return isNaN(parsed) ? "" : parsed.toString();
    };
    
    const currentRate = normalizeRate(transferFormData.transferRate);
    const initialRate = normalizeRate(initialTransferFormData.transferRate);
    
    // Check if any field has changed
    let hasChanges = (
      transferFormData.fromAccountId !== initialTransferFormData.fromAccountId ||
      transferFormData.toAccountId !== initialTransferFormData.toAccountId ||
      currentAmount !== initialAmount ||
      transferFormData.date !== initialTransferFormData.date ||
      transferFormData.time !== initialTransferFormData.time ||
      currentRate !== initialRate ||
      currentFeeAmount !== initialFeeAmount ||
      currentFeeAccountId !== initialFeeAccountId
    );
    if (hasChanges) return true;

    // Compare custom field values
    const currentCustom = transferFormData.customFieldValues || {};
    const initialCustom = initialTransferFormData.customFieldValues || {};
    const customKeys = Array.from(new Set([...Object.keys(currentCustom), ...Object.keys(initialCustom)]));
    for (let ki = 0; ki < customKeys.length; ki++) {
      const key = customKeys[ki];
      const c = currentCustom[key];
      const i = initialCustom[key];
      const cStr = typeof c === "string" ? c : (c && typeof c === "object" && "url" in c ? (c as { url?: string }).url : undefined);
      const iStr = typeof i === "string" ? i : (i && typeof i === "object" && "url" in i ? (i as { url?: string }).url : undefined);
      if (cStr !== iStr) return true;
    }
    if (transferFormData.customFieldPendingFiles && Object.keys(transferFormData.customFieldPendingFiles).length > 0) return true;

    return false;
  };

  return {
    isTransactionFormValid,
    isTransferFormValid,
    hasTransactionChanges,
    hasTransferChanges,
  };
};
